import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

export default function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form | success
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', password2: ''
  });
  const [kids, setKids] = useState([{ id: 1, kid_name: '', kid_username: '' }]);
  const [kidCounter, setKidCounter] = useState(1);
  const [errors, setErrors] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [usernameStatuses, setUsernameStatuses] = useState({});
  const usernameTimers = useRef({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addKid = () => {
    const newId = kidCounter + 1;
    setKidCounter(newId);
    setKids(prev => [...prev, { id: newId, kid_name: '', kid_username: '' }]);
  };

  const removeKid = (id) => {
    if (kids.length <= 1) return;
    setKids(prev => prev.filter(k => k.id !== id));
    setUsernameStatuses(prev => { const c = { ...prev }; delete c[id]; return c; });
  };

  const updateKid = (id, field, value) => {
    setKids(prev => prev.map(k => k.id === id ? { ...k, [field]: value } : k));
    if (field === 'kid_username') checkUsername(id, value);
  };

  const checkUsername = useCallback((kidId, username) => {
    if (usernameTimers.current[kidId]) clearTimeout(usernameTimers.current[kidId]);
    if (!username || username.length < 3) {
      setUsernameStatuses(prev => ({ ...prev, [kidId]: null }));
      return;
    }
    setUsernameStatuses(prev => ({ ...prev, [kidId]: 'checking' }));
    usernameTimers.current[kidId] = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('kid_username_exists', { p_username: username });
        if (error) setUsernameStatuses(prev => ({ ...prev, [kidId]: 'error' }));
        else if (data === true) setUsernameStatuses(prev => ({ ...prev, [kidId]: 'taken' }));
        else setUsernameStatuses(prev => ({ ...prev, [kidId]: 'available' }));
      } catch {
        setUsernameStatuses(prev => ({ ...prev, [kidId]: 'error' }));
      }
    }, 500);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors('');
    if (!formData.name.trim()) return setErrors('Name is required.');
    if (!formData.email.trim()) return setErrors('Email is required.');
    if (!formData.password || formData.password.length < 6) return setErrors('Password must be at least 6 characters.');
    if (formData.password !== formData.password2) return setErrors('Passwords do not match.');
    for (const kid of kids) {
      if (!kid.kid_name.trim()) return setErrors('Every child must have a name.');
      if (!kid.kid_username.trim() || kid.kid_username.length < 3) return setErrors('Every child must have a username (min 3 chars).');
      if (usernameStatuses[kid.id] === 'taken') return setErrors(`Username @${kid.kid_username} is already taken.`);
    }

    setSubmitting(true);
    try {
      // Check if email already exists (via RPC — doesn't leak customer data)
      const { data: emailExists } = await supabase.rpc('customer_exists_by_email', {
        p_email: formData.email.trim().toLowerCase(),
      });
      if (emailExists === true) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }

      // Check if phone already exists (if provided)
      if (formData.phone.trim()) {
        const cleanPhone = formData.phone.trim().replace(/\s+/g, '');
        const { data: phoneExists } = await supabase.rpc('customer_exists_by_phone', {
          p_phone: cleanPhone,
        });
        if (phoneExists === true) {
          throw new Error('An account with this phone number already exists. Please sign in instead.');
        }
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });
      if (authError) throw authError;
      const userId = authData.user?.id;
      // Supabase returns a user with identities=[] if email already exists in auth
      if (!userId || (authData.user?.identities && authData.user.identities.length === 0)) {
        throw new Error('An account with this email already exists. Please sign in or use "Forgot Password".');
      }

      const { data: custResult, error: custError } = await supabase.rpc('create_customer_on_signup', {
        p_auth_id: userId,
        p_name: formData.name,
        p_email: formData.email.trim().toLowerCase(),
        p_phone: formData.phone.trim().replace(/\s+/g, '') || null,
        p_plan: 'free',
      });
      if (custError) throw custError;
      const customerId = custResult?.id;

      for (const kid of kids) {
        const { error: kidError } = await supabase.from('kids').insert({
          customer_id: customerId,
          kid_name: kid.kid_name,
          kid_username: kid.kid_username,
        });
        if (kidError) throw kidError;
      }

      const { error: settingsError } = await supabase.from('settings').insert({
        user_id: userId,
        parent_id: userId,
        screenshot_interval: 10,
        screen_time_limit: 180,
        bedtime_start: '22:00',
        bedtime_end: '07:00',
      });
      if (settingsError) console.warn('Settings insert warning:', settingsError);

      setStep('success');
    } catch (err) {
      setErrors(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    background: '#0f172a', border: '1.5px solid #334155',
    borderRadius: 10, color: '#fff', fontSize: 14,
    outline: 'none', fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 600,
    color: '#94a3b8', marginBottom: 6,
  };

  if (step === 'success') {
    return (
      <div style={{
        minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a, #1e293b)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}>
        <div style={{
          background: '#1e293b', borderRadius: 20, padding: '48px 40px',
          width: '100%', maxWidth: 480, boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
          border: '1px solid #334155', textAlign: 'center',
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>{'\u2705'}</div>
          <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: '0 0 8px' }}>
            Welcome to ParentsProtection!
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 24 }}>
            Your account has been created. Check your email to verify, then install the app on your child's Mac.
          </p>
          <div style={{ marginBottom: 24 }}>
            {kids.map(kid => (
              <div key={kid.id} style={{
                background: '#0f172a', borderRadius: 10, padding: '10px 16px',
                marginBottom: 8, display: 'flex', justifyContent: 'space-between',
                color: '#e2e8f0', fontSize: 14,
              }}>
                <span style={{ fontWeight: 600 }}>{kid.kid_name}</span>
                <span style={{ color: '#0ea5e9' }}>@{kid.kid_username}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/login')}
            style={{
              width: '100%', padding: '14px',
              background: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
              color: '#fff', border: 'none', borderRadius: 10,
              fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Go to Login {'\u2192'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a, #1e293b)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter', -apple-system, sans-serif", padding: '40px 16px',
    }}>
      <div style={{
        background: '#1e293b', borderRadius: 20, padding: '40px 36px',
        width: '100%', maxWidth: 520, boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
        border: '1px solid #334155',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>{'\uD83D\uDEE1\uFE0F'}</div>
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>
            Create Your Account
          </h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
            Start protecting your child's digital life
          </p>
        </div>

        {errors && (
          <div style={{
            background: '#fee2e2', color: '#dc2626', padding: '10px 14px',
            borderRadius: 10, fontSize: 13, marginBottom: 16, textAlign: 'center',
          }}>
            {errors}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Full Name</label>
            <input type="text" placeholder="Your full name" value={formData.name}
              onChange={e => handleChange('name', e.target.value)} style={inputStyle} />
          </div>

          {/* Email + Phone */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" placeholder="you@example.com" value={formData.email}
                onChange={e => handleChange('email', e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone (optional)</label>
              <input type="tel" placeholder="+91 98765 43210" value={formData.phone}
                onChange={e => handleChange('phone', e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Password */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" placeholder="Min 6 characters" value={formData.password}
                onChange={e => handleChange('password', e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <input type="password" placeholder="Repeat password" value={formData.password2}
                onChange={e => handleChange('password2', e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Children */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 12, marginTop: 8,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>Children</span>
            <button type="button" onClick={addKid} style={{
              background: 'rgba(14,165,233,0.1)', color: '#0ea5e9', border: '1px solid rgba(14,165,233,0.3)',
              borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>+ Add Child</button>
          </div>

          {kids.map(kid => (
            <div key={kid.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10,
              marginBottom: 10, alignItems: 'end',
            }}>
              <div>
                <label style={labelStyle}>Child Name</label>
                <input type="text" placeholder="e.g. Aarav" value={kid.kid_name}
                  onChange={e => updateKid(kid.id, 'kid_name', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Username</label>
                <input type="text" placeholder="e.g. aarav2015" value={kid.kid_username}
                  onChange={e => updateKid(kid.id, 'kid_username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  style={{
                    ...inputStyle,
                    borderColor: usernameStatuses[kid.id] === 'available' ? '#22c55e'
                      : usernameStatuses[kid.id] === 'taken' ? '#ef4444' : '#334155',
                  }} />
                {usernameStatuses[kid.id] === 'checking' && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Checking...</div>
                )}
                {usernameStatuses[kid.id] === 'available' && (
                  <div style={{ fontSize: 11, color: '#22c55e', marginTop: 4 }}>@{kid.kid_username} is available</div>
                )}
                {usernameStatuses[kid.id] === 'taken' && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>@{kid.kid_username} is taken</div>
                )}
              </div>
              <button type="button" onClick={() => removeKid(kid.id)} style={{
                width: 36, height: 36, background: 'rgba(239,68,68,0.1)',
                color: '#f87171', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 8, fontSize: 18, cursor: 'pointer',
                visibility: kids.length > 1 ? 'visible' : 'hidden',
                marginBottom: 2,
              }}>{'\u00D7'}</button>
            </div>
          ))}

          <button type="submit" disabled={submitting} style={{
            width: '100%', padding: '14px', marginTop: 16,
            background: submitting ? '#334155' : 'linear-gradient(135deg, #0ea5e9, #2563eb)',
            color: '#fff', border: 'none', borderRadius: 10,
            fontSize: 15, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer',
            fontFamily: 'inherit',
          }}>
            {submitting ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#64748b' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#0ea5e9', textDecoration: 'none', fontWeight: 600 }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
